import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";

export function AuthLayout({ children, title, subtitle }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <div className="mx-auto w-full max-w-md px-6 pb-6 pt-14">
        <img src="/brand/avelis-logo.png" alt="AVELIS by DK Soins SRL" className="h-auto w-[180px]" data-testid="brand-logo" />
        <div className="mt-1 text-sm font-semibold uppercase tracking-[0.2em] text-teal-600">Planning</div>
        <h1 className="mt-10 text-3xl font-bold leading-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-slate-500">{subtitle}</p>}
      </div>
      <div className="mx-auto w-full max-w-md flex-1 px-4 pb-10">
        <div className="card p-6 fade-up">{children}</div>
        <p className="mt-6 text-center text-xs text-slate-500">Gratuit pour les infirmières indépendantes · <Link to="/legal" className="underline">Confidentialité</Link></p>
      </div>
    </div>
  );
}

export default function Login() {
  const { me, setMe } = useApp();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const next = params.get("next") || "/";
  if (me) return <Navigate to={me.user?.is_superadmin && next === "/" ? "/admin" : next} replace />;

  const done = (data) => { setMe(data); nav(data.user.is_superadmin && next === "/" ? "/admin" : next, { replace: true }); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) setMfa(data); else done(data);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { done((await api.post("/auth/login/verify", { challenge_id: mfa.challenge_id, code })).data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const resend = async () => { try { await api.post("/auth/login/resend", { challenge_id: mfa.challenge_id }); toast.success("Nouveau code envoyé"); } catch (err) { toast.error(errMsg(err)); } };

  if (mfa) return (
    <AuthLayout title="Vérification" subtitle={`Un code à 6 chiffres a été envoyé à ${mfa.email_hint}.`}>
      <form onSubmit={verify} className="space-y-4" data-testid="mfa-form">
        <input className="field text-center text-2xl tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="mfa-code-input" />
        <button className="btn-primary w-full" disabled={busy || code.length !== 6} data-testid="mfa-submit-btn">{busy ? "Vérification…" : "Valider"}</button>
        <button type="button" className="block w-full py-2 text-center text-sm font-semibold text-teal-600" onClick={resend} data-testid="mfa-resend-btn">Renvoyer un code</button>
      </form>
    </AuthLayout>
  );

  return (
    <AuthLayout title="Connexion" subtitle="Vos disponibilités, votre planning, vos missions.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <label className="block"><span className="label">Adresse e-mail</span>
          <input className="field" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" /></label>
        <label className="block"><span className="label">Mot de passe</span>
          <input className="field" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" /></label>
        <button className="btn-primary w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Connexion…" : "Se connecter"}</button>
        <Link to="/mot-de-passe-oublie" className="block py-2 text-center text-sm font-semibold text-teal-600" data-testid="login-forgot-link">Mot de passe oublié ?</Link>
      </form>
      <div className="mt-2 border-t border-slate-100 pt-4">
        <Link to={`/inscription${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="btn-ghost w-full" data-testid="login-register-link">Créer un compte gratuit</Link>
      </div>
    </AuthLayout>
  );
}
